/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useContext, useEffect, useState, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import { LayoutContext } from "../../components/layout";
import Pagination from "../../components/Pagination";
import fetchHelper from "../../services/fetchHelper";
import AllReportsFilter from "./AllReportsFilter";
import InsuranceTable from "./InsuranceTable";
import PatientMergeTable from "./PatientMergeTable";
import PatientsTable from "./PatientsTable";
import PendingTable from "./PendingTable";
import { calculateAge } from "./PatientsTable";
import useError from "../../hooks/useError";
import { useHistory } from "react-router-dom";
import {
  mapKeyFilters,
  mapKeyFiltersForPatientReport,
  mapKeyFiltersForInsurrance,
} from "../../constants/PatientListFilterConstants";
import { patientsHeaderCsv, insuranceHeaderCsv } from "../../constants/HeaderCsvConstants";
import "./AllReports.css";
import { CSVLink } from "react-csv";
import { GET_PATIENTS_API_V2_ENABLED } from "../../constants/AppConfigConstants";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export const Loading = ({ length = 15, colspan = 12 }) => {
  const array = new Array(length);
  array.fill(1);
  return array.map((item, index) => (
    <tbody key={index}>
      <tr>
        <td colSpan={colspan}></td>
      </tr>
      <tr>
        <td colSpan={colspan} style={{ background: "#fff", padding: 0 }}>
          <img src="data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMTUwNSA0NSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHN0eWxlIHR5cGU9InRleHQvY3NzIj4KLnJlY3QgewpmaWxsOiB1cmwoI2xpbmVhci1ncmFkaWVudCk7Cn0KPC9zdHlsZT4KPGRlZnM+CjxsaW5lYXJHcmFkaWVudCBpZD0ibGluZWFyLWdyYWRpZW50Ij4KPHN0b3Agb2Zmc2V0PSIwJSIgc3RvcC1jb2xvcj0iI0Y3RjdGNyIgLz4KPHN0b3Agb2Zmc2V0PSIzMyUiIHN0b3AtY29sb3I9IiNGN0Y3RjciIC8+CjxzdG9wIG9mZnNldD0iNTAlIiBzdG9wLWNvbG9yPSIjZWZmMGYwIiAvPgo8c3RvcCBvZmZzZXQ9IjY3JSIgc3RvcC1jb2xvcj0iI0Y3RjdGNyIgLz4KPHN0b3Agb2Zmc2V0PSIxMDAlIiBzdG9wLWNvbG9yPSIjRjdGN0Y3IiAvPgo8YW5pbWF0ZVRyYW5zZm9ybSBhdHRyaWJ1dGVOYW1lPSJncmFkaWVudFRyYW5zZm9ybSIKdHlwZT0idHJhbnNsYXRlIgpmcm9tPSItMSAwIgp0bz0iMSAwIgpiZWdpbj0iMHMiCmR1cj0iMS41cyIKcmVwZWF0Q291bnQ9ImluZGVmaW5pdGUiLz4KPC9saW5lYXJHcmFkaWVudD4KPC9kZWZzPgoKPHJlY3QgY2xhc3M9InJlY3QiIHg9IjAuOTgyOTEiIHk9IjAuMTY3NDgiIHdpZHRoPSIxNTAzLjE1IiBoZWlnaHQ9IjQ0LjgzMjUiIHJ4PSI2IiBmaWxsPSIjZWZmMGYwIi8+CjxyZWN0IGNsYXNzPSJyZWN0IiB4PSIwLjk4MjkxIiB5PSIwLjE2NzQ4IiB3aWR0aD0iMTUwMy4xNSIgaGVpZ2h0PSI0NC44MzI1IiByeD0iNiIgZmlsbD0idXJsKCNwYWludDBfbGluZWFyKSIvPgo8L3N2Zz4=" />
        </td>
      </tr>
    </tbody>
  ));
};

const Table = ({ tab, data, initialLoad, mergePatients, onMergePatients, sortingFunction }) => {
  switch (tab) {
    case "insurance":
      return <InsuranceTable data={data} initialLoad={initialLoad} sortingFunction={sortingFunction} />;
    case "pending":
      return <PendingTable data={data} initialLoad={initialLoad} sortingFunction={sortingFunction} />;
    case "patient-merge":
      return (
        <PatientMergeTable
          data={data}
          initialLoad={initialLoad}
          mergePatients={mergePatients}
          onMergePatients={onMergePatients}
          sortingFunction={sortingFunction}
        />
      );
    default:
      return <PatientsTable data={data} initialLoad={initialLoad} sortingFunction={sortingFunction} />;
  }
};

const AllReports = ({ cancelRequest, ...props }) => {
  const { user, appConfigStructure } = useContext(LayoutContext);
  const history = useHistory();
  const filterByMrn = history.location.state?.filters;
  const [textSearchUI, setTextSearchUI] = useState("");
  const [showAdvance, setShowAdvance] = useState(false);
  const [data, setData] = useState([]);
  const [to, setTo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  const wildcard = useRef("");
  const filter = useRef(null);
  const offset = useRef(0);
  const url = useRef("");
  const limit = useRef(30);
  const sort = useRef(null);
  const initOffset = useRef(true);
  const defaultFilter = useRef(null);
  const customFilters = useRef(null);
  const showDefaultApiError = useError();
  const inputRef = useRef("");
  const { tab } = useParams();
  const query = useQuery();

  const setWildcard = (value) => {
    wildcard.current = value;
  };

  const setTextSearch = (value) => {
    inputRef.current = value;
    setTextSearchUI(value);
  };

  const setOffsetEvent = (value) => {
    setOffset(value);
    setIsLoading(true);
    setInitialLoad(true);
    localStorage.removeItem("patients-offset");
    localStorage.setItem("patients-offset", offset.current);
    fetchData();
  };
  const setOffset = (value) => {
    offset.current = value;
  };

  const setFilter = (value) => {
    filter.current = value;
  };
  const onSetFilterEvent = (value) => {
    setFilter(value);
    setFilterSelected();
    fetchData();
  };

  const setDefaultSortOrder = () => {
    const tab = localStorage.getItem("selectedTab");
    switch (tab) {
      case "insurance":
        sort.current = ["-id"];
        return;
      case "pending":
        sort.current = ["-id"];
        return;
      case "patient-merge":
        sort.current = ["-id"];
        return;
      default:
        sort.current = ["-createdAt"];
        return;
    }
  };

  const sortingFunction = (field) => {
    setIsLoading(true);
    setInitialLoad(true);
    if (field === null) {
      setDefaultSortOrder();
    } else {
      sort.current = [field];
    }
    fetchData();
  };

  useEffect(() => {
    setData([]);
    if (user) {
      setFilter(null);
      setWildcard("");

      const tab = localStorage.getItem("selectedTab");
      const location = localStorage.getItem("location");
      let textSearchValue = "";
      if (location) {
        textSearchValue = localStorage.getItem(tab + location) || "";
      } else {
        textSearchValue = localStorage.getItem(tab) || "";
      }

      switch (tab) {
        case "insurance":
          url.current = `/patientmanagement/rest/insurance`;
          customFilters.current = mapKeyFiltersForInsurrance;
          defaultFilter.current = {};
          break;
        case "pending":
          url.current = `/patientmanagement/rest/patients/getPendingPatients`;
          customFilters.current = mapKeyFilters;
          defaultFilter.current = {};
          break;
        case "patient-merge":
          url.current = `/patientmanagement/rest/patients/availableToMerge`;
          customFilters.current = mapKeyFilters;
          defaultFilter.current = {};
          break;
        default:
          if (appConfigStructure[GET_PATIENTS_API_V2_ENABLED]) {
            url.current = `/patientmanagement/rest/v2/patients`;
            customFilters.current = mapKeyFiltersForPatientReport;
          } else {
            url.current = `/patientmanagement/rest/patients`;
            customFilters.current = mapKeyFilters;
          }

          defaultFilter.current = {};
          break;
      }
      setDefaultSortOrder();
      if (textSearchValue) {
        setTextSearch(textSearchValue);
        onSearchSubmittion();
      } else {
        setTextSearch("");
      }
      if (filterByMrn !== undefined) {
        setFilter(filterByMrn);
        let defaultFilterDuplicatedMrn = Object.keys(filterByMrn)
          .map((key) => `patientLocation.mrn:${filterByMrn[key].value}`)
          .join("");
        setTextSearch(defaultFilterDuplicatedMrn);
        localStorage.setItem(`${tab}${location}`, defaultFilterDuplicatedMrn);
        window.history.replaceState({}, document.title);
      }
      setInitialLoad(true);
      setIsLoading(true);

      if (query.get("return") && localStorage.getItem("patients-offset") != null) {
        const patientsOffset = Number(localStorage.getItem(`patients-offset`));
        if (offset.current != patientsOffset) {
          initOffset.current = false;
          setOffset(patientsOffset);
        }
      } else if (offset.current !== 0) {
        setOffset(0);
        localStorage.removeItem("patients-offset");
      }
      fetchData();
    }
  }, [tab, user]);

  const onSearchSubmittion = (textSearchValue) => {
    let filters = {};
    const location = localStorage.getItem("location");
    const tab = localStorage.getItem("selectedTab");

    if (textSearchValue == "") {
      if (location) {
        localStorage.setItem(tab + location, "");
      } else {
        localStorage.setItem(tab, "");
      }
    }

    if (textSearchValue === undefined) {
      if (location) {
        textSearchValue = localStorage.getItem(tab + location) || "";
      } else {
        textSearchValue = localStorage.getItem(tab) || "";
      }
      setTextSearch(textSearchValue);
    }

    if (textSearchValue) {
      textSearchValue.split(" ").forEach((i) => {
        const filterSplit = i.split(":");
        //To make sure that the array contains more than one element (key + value).
        if (filterSplit.length > 1) {
          const mapKeyFilter = customFilters.current[filterSplit[0]];
          filters[filterSplit[0]] = { value: filterSplit[1], ...mapKeyFilter };
        }
      });
      if (Object.keys(filters).length === 0) {
        const wildCard = textSearchValue
          .replace(/\s\s+/g, " ")
          .split(" ")
          .map((ts) => ({
            [customFilters.current.fname.key]: { [customFilters.current.fname.com]: ts },
            [customFilters.current.lname.key]: { [customFilters.current.lname.com]: ts },
          }));
        setTextSearch(textSearchValue.replace(/\s\s+/g, " "));
        setWildcard(wildCard);
      }
      setIsLoading(true);
    } else {
      setOffset(0);
      setWildcard("");
    }
    setFilter(filters);
  };

  const setFilterSelected = () => {
    const location = localStorage.getItem("location");
    if (filter.current) {
      setIsLoading(true);
      setInitialLoad(true);
      const result = Object.keys(filter.current)
        .map((key) => `${key}:${filter.current[key].value}`)
        .join(" ");
      if (result) setTextSearch(result);
      const tab = localStorage.getItem("selectedTab");
      if (result) {
        if (location) {
          localStorage.setItem(tab + location, result);
        } else {
          localStorage.setItem(tab, result);
        }
      } else {
        let textSearchValue = "";
        if (location) {
          textSearchValue = localStorage.getItem(tab + location) || "";
        } else {
          textSearchValue = localStorage.getItem(tab) || "";
        }
        setTextSearch(textSearchValue);
      }
      if (Object.keys(filter.current).length === 0) {
        if (inputRef.current.search(":") !== -1) setTextSearch("");
        else setTextSearch(inputRef.current);
      }
    } else setTextSearch(inputRef.current);
  };

  const fetchData = async () => {
    try {
      const where = [];
      Object.keys(defaultFilter.current).map((f) => {
        const data = defaultFilter.current[f];
        where.push({ [data.key]: { [data.com]: data.value } });
      });

      if (wildcard.current) where.push(...wildcard.current);

      if (filter.current) {
        Object.keys(filter.current).forEach((fKey) => {
          const { key, com, value } = filter.current[fKey];
          where.push({ [key]: { [com]: value } });
        });
      }

      const urlParams = {
        where: JSON.stringify(where),
        match: "AND",
        matchInner: "OR",
        sort: sort.current.join(","),
      };
      const urlSearch = new URLSearchParams(urlParams);
      const data = await fetchHelper.get({
        url: `${url.current}?${urlSearch}`,
        headers: {
          "X-Range": `items=${offset.current}-${offset.current + limit.current - 1}`,
          Range: `items=${offset.current}-${offset.current + limit.current - 1}`,
        },
        options: {
          signal: cancelRequest.signal,
        },
      });
      setTo(offset.current + data.length - 1);
      setData(data);
    } catch (err) {
      showDefaultApiError(err);
      setData([]);
    } finally {
      setInitialLoad(false);
      setIsLoading(false);
      initOffset.current = false;
    }
  };

  const physicianFullName = (info) => (info ? `${info.family}, ${info.name}` : "No data");
  const subscriberFullName = (info) => {
    if (info.subscriberLastname && info.subscriberFirstname) {
      return `${info.subscriberLastname}, ${info.subscriberFirstname}`;
    }
    if (info.subscriberLastname || info.subscriberFirstname) {
      return info.subscriberLastname || info.subscriberFirstname;
    }
    return "No data";
  };

  const patientsCsv = data.map((item) => {
    let location;
    let mrn;
    let followingPhysicianData = {
      family: "",
      name: "",
    };

    if (appConfigStructure[GET_PATIENTS_API_V2_ENABLED]) {
      mrn = item.patientLocationMrn;
      location = item.patientLocationName;
      followingPhysicianData.name = item.followingPhysicianName;
      followingPhysicianData.family = item.followingPhysicianFamily;
    } else {
      mrn = item.patientLocation && item.patientLocation.map((elem) => elem.mrn || "No data");
      location = item.patientLocation && item.patientLocation.map((elem) => elem.location.name);
      followingPhysicianData = item.followingPhysician;
    }

    return localStorage.getItem("selectedTab") === "patients"
      ? {
          date: new Date(item.createdAt).toLocaleDateString() || "No data",
          lastName: item.family || "No data",
          firstName: item.name || "No data",
          age: calculateAge(item.dob) || "0",
          gender: item.gender || "No data",
          homePhone: item.homePhone || "No data",
          mobilePhone: item.mobilePhone || "No data",
          email: item.emailAddress || "No data",
          mrn: mrn,
          following: physicianFullName(followingPhysicianData),
          location: location,
        }
      : localStorage.getItem("selectedTab") === "insurance"
      ? {
          date: item.patient ? new Date(item.patient.createdAt).toLocaleDateString() : "No data",
          lastName: item.patient ? item.patient.family : "No data",
          firstName: item.patient ? item.patient.name : "No data",
          age: item.patient ? calculateAge(item.patient.dob) : "No data",
          gender: item.patient?.gender || "No data",
          homePhone: item.patient?.homePhone || "No data",
          mobilePhone: item.patient?.mobilePhone || "No data",
          insurance: item.insuranceName || "No data",
          policy: item.policyNumber || "No data",
          group: item.groupNumber || "No data",
          subscriber: subscriberFullName(item),
          subscriberDob: new Date(item.subscriberDOB).toLocaleDateString() || "No data",
          physician: physicianFullName(item.patient?.followingPhysician),
        }
      : null;
  });

  return (
    <>
      <div className="content">
        <div className="searchAdv" id="showSearch">
          <div className="search">
            <div className="row rowStretch searchIn framing">
              <div className="col col-s-100 col-m-70 col-l-60 col60 searchInCol">
                <label>
                  <input
                    id="search-box"
                    data-testid="search-box"
                    className="textFieldSearch"
                    type="text"
                    placeholder="Type any term(s) to begin search..."
                    value={textSearchUI}
                    ref={inputRef}
                    onChange={(e) => setTextSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.keyCode === 13) {
                        setIsLoading(true);
                        setInitialLoad(true);
                        setTextSearch(e.target.value);
                        onSearchSubmittion(e.target.value);
                        fetchData();
                      }
                    }}
                  />
                  <button
                    className="searchField"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsLoading(true);
                      setInitialLoad(true);
                      onSearchSubmittion(inputRef.current);
                      fetchData();
                    }}
                  >
                    {isLoading ? (
                      <div
                        id="results-loading-icon"
                        className="saving-white-ico icon-64"
                        style={{ height: 18, width: 18, filter: "invert()" }}
                      />
                    ) : (
                      <div
                        id="search-btn"
                        className="search-icon icon-64"
                        style={{ filter: "invert()", width: "15px", height: "15px" }}
                      />
                    )}
                  </button>
                </label>
              </div>
              <div className="col col-s-100 col-m-20 col-l-10 col10 advSearchCol patientAdvSearch">
                <div
                  className="col col50 colBtnAdvSearch"
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  {(localStorage.getItem("selectedTab") === "patients" ||
                    localStorage.getItem("selectedTab") === "insurance") &&
                  !isLoading ? (
                    <CSVLink
                      data={patientsCsv}
                      headers={
                        localStorage.getItem("selectedTab") === "patients"
                          ? patientsHeaderCsv
                          : insuranceHeaderCsv
                      }
                      filename={
                        localStorage.getItem("selectedTab") === "patients"
                          ? "PatientsReport.csv"
                          : "InsuranceReport.csv"
                      }
                      className="csv-link-patients"
                    >
                      <button className="btn btnTeal hideAdv">Download CSV</button>
                    </CSVLink>
                  ) : null}
                  <button
                    id="show-advanced-search-btn"
                    className="btn btnTeal hideAdv"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-around",
                      maxWidth: "200px",
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      setShowAdvance(!showAdvance);
                    }}
                  >
                    <div id="advanced-search-btn" className="search-icon icon-64"></div>{" "}
                    {showAdvance ? "Hide" : "Show"} Advanced
                  </button>
                  <button
                    id="clear-btn"
                    className="btn btnTeal hideAdv"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-around",
                      maxWidth: "200px",
                    }}
                    onClick={(e) => {
                      setIsLoading(true);
                      setInitialLoad(true);
                      const tab = localStorage.getItem("selectedTab");
                      const location = localStorage.getItem("location");
                      localStorage.removeItem(tab + location);
                      setTextSearch("");
                      onSearchSubmittion();
                      fetchData();
                      e.preventDefault();
                    }}
                  >
                    {" "}
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </div>
          {showAdvance ? (
            <AllReportsFilter
              onFilterChange={(filter) => onSetFilterEvent(filter)}
              filter={filter.current}
              activeTab={tab}
            />
          ) : null}
        </div>
      </div>

      <Table
        tab={tab}
        data={data}
        initialLoad={initialLoad}
        mergePatients={props.mergePatients}
        onMergePatients={props.onMergePatients}
        sortingFunction={sortingFunction}
      />
      <Pagination
        offset={offset.current}
        to={to}
        limit={limit.current}
        onSetOffset={setOffsetEvent}
        loading={initialLoad}
      />
    </>
  );
};

export default AllReports;
